// @flow
import React, { type Element } from 'react';
import { connect } from 'react-redux';
import { compose, pure } from 'recompose';
import { hot } from 'react-hot-loader';
import { Grid, Row, Col } from 'angieslist-react-components';
import { urlCatalog, analytics } from 'angieslist-app-platform';
import Helmet from 'react-helmet-async';
import { get, find } from 'lodash';
import FAQAnalytics from './FAQAnalytics';
import './faqView.scss';
import performanceActions from '../common/performanceActions';
import type { PerformanceReducerState } from '../common/performanceReducer';
import type { ServiceProviderCacheState } from '../../../app/types';

export type PerformanceFAQProps = {
  serviceProviderCache: ServiceProviderCacheState,
  performance: PerformanceReducerState,
  getContactMetricsByServiceProvider: typeof performanceActions.getContactMetricsByServiceProvider,
  params: {
    serviceProviderId: number
  },
};

export class FAQViewComponent extends React.Component<PerformanceFAQProps> {
  componentDidMount() {
    const endTime = new Date();
    const startTime = new Date(new Date().setDate(endTime.getDate() - 90));
    const { serviceProviderId } = this.props.params;
    const { getContactMetricsByServiceProvider } = this.props;
    getContactMetricsByServiceProvider({ serviceProviderId, startTime, endTime });
    window.scrollTo(0, 0);
  }
  props: PerformanceFAQProps;
  render(): Element<"div"> {
    const {
      serviceProviderCache, performance: {
        contactMetrics: {
          response: contactMetrics,
        },
      },
    } = this.props;

    const typeMetrics = get(contactMetrics, 'contactTypeMetrics', {});
    const callsMetrics = find(typeMetrics, o => o.contactType === 'Calls');
    const estimatedCallsMetrics = find(typeMetrics, o => o.contactType === 'Estimated Calls');

    const callCount = get(callsMetrics, 'contactCount', 0);
    const estimatedCallCount = get(estimatedCallsMetrics, 'contactCount', 0);
    const totalCallCount = callCount + estimatedCallCount;

    const trackedCallPercentage =
      Math.round(totalCallCount > 0 ? (callCount / totalCallCount) * 100 : 0);

    const { params } = this.props;
    const serviceProviderId = get(params, 'serviceProviderId');

    const currentSP = serviceProviderCache[serviceProviderId];
    const isAdvertiser = get(currentSP, 'isAdvertiser', false);

    return (
      <div style={{ marginTop: '-1.25em' }} className="faq-container">
        <Grid>
          <Helmet title="Performance FAQ" />
          <Row>
            <Col xs={12}>
              <h1>Performance Metrics</h1>
              <h3 style={{ marginBottom: '1.5em' }}>Frequently Asked Questions</h3>

              <h4>What are Customer Contacts?</h4>
              <p>The <em>overall</em> Customer Contacts metric represents the total count
              of contacts the registered business received over the designated time period
              minus any duplicate contacts sent by the same customer on the same day.
              If the same customer contacts the business twice in one day, Customer Contacts
              will only increase by one for that interaction. For example, if the same customer
              submits a Request a Quote and then calls through Angi on the same day
              for your business, Angi will only count the pair of contacts as
              a single Customer Contact. Messages in Message center are a means to communicate with
              customers who Request a Quote or Claim a Deal and each individual message
              does not count as a unique contact.
              </p>

              <If condition={isAdvertiser}>
                <h4>What are category Customer Contacts?</h4>
                <p>On the &quot;Performance&quot; page, you&apos;ll see a more detailed breakdown
                of your Customer Contacts. The number of Customer Contacts in a category group
                represents the total unique customers by day that contact your business over the
                time period <em>in a specific category group</em>. The same deduplication rules
                apply, but only for contacts in the same category group. If one customer contacts
                you for two or more category groups in one day, that customer will be counted
                uniquely in each <em>specific category group</em>, but only as one <em>overall</em>
                &nbsp;Customer Contact.
                </p>
              </If>
              <p>Around the Customer Contacts numbers, you&apos;ll see a donut graph. This
              represents the contact method distribution. You can use this to see how your
              customers have chosen to contact your business through the methods described below.
              </p>

              <If condition={isAdvertiser}>
                <h4>What are Estimated Calls?</h4>
                <p>
                We can attribute most member phone calls to Angi Certified Pros due to our
                tracked phone numbers. However, some members contact Certified Pros outside our
                platform in a way we cannot directly track. Over a third of Angi members we
                sampled use the site to confirm recommendations from friends, neighbors, or family
                members, so they might already have your phone number. After a thorough analysis of
                our nationwide site traffic and reviews left without trackable contacts over the
                past year, we estimate on average how frequently this occurs.
                  <If condition={totalCallCount > 0}> Of the calls metric
                  shown, {trackedCallPercentage}% are due to tracked phone calls. The remainder are
                  from phone calls we believe happened off of our platform.
                  </If>
                </p>

                <em>How are calls assigned a category?</em>
                <p>Angi members are required to click to reveal your phone number,
                and this lets us know what categories they are likely to be contacting your
                business about. We use this data to estimate what categories your calls were about.
                </p>
              </If>

              <h4>What are Quote Requests?</h4>
              <p>Throughout our site, Angi members can click on
              &apos;Request a Quote&apos; buttons that lead them down a multistep funnel
              that asks them several questions about their project and requires them to send
              their phone number and email address. These are shown on the&nbsp;
                <a
                  id="performance-faq--what-are-requests-lead"
                  href={urlCatalog.office.leadBoard(serviceProviderId)}
                >
                  Lead Board
                </a>
                &nbsp;and are labeled &quot;Quote Request.&quot;
              </p>

              <h4>What are Claimed Deals?</h4>
              <p>We show Angi members available deals to claim in a variety of places on
              our site including the &apos;Shop Deals&apos; section and the featured spots on the
              top of search results. Your Client Success Manager can help you set up a Deal if you
              don’t currently have one. These are shown on the&nbsp;
                <a
                  id="performance-faq--what-are-deals-lead"
                  href={urlCatalog.office.leadBoard(serviceProviderId)}
                >
                  Lead Board
                </a>
                &nbsp;and are labeled &quot;Claimed Deal.&quot;
              </p>

            </Col>
          </Row>
        </Grid>
      </div>
    );
  }
}

export default (hot(module)(compose(
  analytics({
    ruleset: FAQAnalytics,
  }),
  connect(state => ({
    performance: state.performance,
    serviceProviderCache: state.serviceProviderCache,
  }), {
    ...performanceActions,
  }),
  pure,
)(FAQViewComponent)): any);



// WEBPACK FOOTER //
// ./src/modules/home/browser/FAQView.js